export function defaultBrandProfile() {
  return {
    businessName: '',
    businessDescription: '',
    productOrService: '',
    targetAudience: '',
    offer: '',
    goalsText: '',
    positioning: '',
    uniqueSellingPoints: [],
    objections: [],
    buyerPains: [],
    conversionActions: [],
    toneOfVoice: 'professional',
    customToneDescription: '',
    wordsToUse: [],
    wordsToAvoid: [],
    contentPillars: [],
    forbiddenTopicsText: '',
    complianceBoundaries: [],
    visualStyle: 'premium_dark',
    brandColors: [],
    referenceAssetIds: [],
    logoAssetId: '',
    avoidVisuals: [],
    preferredImageMood: '',
    imageCompositionHints: [],
    preferredPlatformsText: '',
    platformPriorityText: '',
    perPlatformToneText: '',
    perPlatformFormatPreferencesText: '',
    socialConnectStatus: {},
    websiteUrl: '',
    websiteAnalysis: null,
    referenceAssets: [],
    sourceMetadata: {},
    manualOverrides: [],
    language: 'en',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  };
}

export function toBrandForm(brandProfile = {}) {
  return {
    ...defaultBrandProfile(),
    businessName: brandProfile.businessName || '',
    businessDescription: brandProfile.businessDescription || '',
    productOrService: brandProfile.productOrService || '',
    targetAudience: brandProfile.targetAudience || '',
    offer: brandProfile.offer || '',
    goalsText: (brandProfile.goals || []).join(', '),
    positioning: brandProfile.positioning || '',
    uniqueSellingPoints: Array.isArray(brandProfile.uniqueSellingPoints) ? brandProfile.uniqueSellingPoints : [],
    objections: Array.isArray(brandProfile.objections) ? brandProfile.objections : [],
    buyerPains: Array.isArray(brandProfile.buyerPains) ? brandProfile.buyerPains : [],
    conversionActions: Array.isArray(brandProfile.conversionActions) ? brandProfile.conversionActions : [],
    toneOfVoice: brandProfile.toneOfVoice || 'professional',
    customToneDescription: brandProfile.customToneDescription || '',
    wordsToUse: Array.isArray(brandProfile.wordsToUse) ? brandProfile.wordsToUse : [],
    wordsToAvoid: Array.isArray(brandProfile.wordsToAvoid) ? brandProfile.wordsToAvoid : [],
    contentPillars: Array.isArray(brandProfile.contentPillars) ? brandProfile.contentPillars : [],
    forbiddenTopicsText: (brandProfile.forbiddenTopics || []).join(', '),
    complianceBoundaries: Array.isArray(brandProfile.complianceBoundaries) ? brandProfile.complianceBoundaries : [],
    visualStyle: brandProfile.visualStyle || 'premium_dark',
    brandColors: Array.isArray(brandProfile.brandColors) ? brandProfile.brandColors : [],
    referenceAssetIds: Array.isArray(brandProfile.referenceAssetIds) ? brandProfile.referenceAssetIds : [],
    logoAssetId: brandProfile.logoAssetId || '',
    avoidVisuals: Array.isArray(brandProfile.avoidVisuals) ? brandProfile.avoidVisuals : [],
    preferredImageMood: brandProfile.preferredImageMood || '',
    imageCompositionHints: Array.isArray(brandProfile.imageCompositionHints) ? brandProfile.imageCompositionHints : [],
    preferredPlatformsText: (brandProfile.preferredPlatforms || []).join(', '),
    platformPriorityText: (brandProfile.platformPriority || []).join(', '),
    perPlatformToneText: objectToLines(brandProfile.perPlatformTone || {}),
    perPlatformFormatPreferencesText: objectToLines(brandProfile.perPlatformFormatPreferences || {}),
    socialConnectStatus: brandProfile.socialConnectStatus || {},
    websiteUrl: brandProfile.websiteUrl || '',
    websiteAnalysis: brandProfile.websiteAnalysis || null,
    referenceAssets: Array.isArray(brandProfile.referenceAssets) ? brandProfile.referenceAssets : [],
    sourceMetadata: brandProfile.sourceMetadata || {},
    manualOverrides: Array.isArray(brandProfile.manualOverrides) ? brandProfile.manualOverrides : [],
    language: brandProfile.language || 'en',
    timezone: brandProfile.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  };
}

export function fromBrandForm(profile) {
  return {
    businessName: profile.businessName || null,
    businessDescription: profile.businessDescription,
    productOrService: profile.productOrService || null,
    targetAudience: profile.targetAudience,
    offer: profile.offer || null,
    goals: splitCsv(profile.goalsText, []),
    positioning: profile.positioning || null,
    uniqueSellingPoints: cleanArray(profile.uniqueSellingPoints),
    objections: cleanArray(profile.objections),
    buyerPains: cleanArray(profile.buyerPains),
    conversionActions: cleanArray(profile.conversionActions),
    toneOfVoice: profile.toneOfVoice,
    customToneDescription: profile.customToneDescription || null,
    wordsToUse: cleanArray(profile.wordsToUse),
    wordsToAvoid: cleanArray(profile.wordsToAvoid),
    contentPillars: profile.contentPillars.map((item) => String(item).trim()).filter(Boolean),
    forbiddenTopics: splitCsv(profile.forbiddenTopicsText, []),
    complianceBoundaries: cleanArray(profile.complianceBoundaries),
    visualStyle: profile.visualStyle,
    brandColors: cleanArray(profile.brandColors),
    referenceAssetIds: cleanArray(profile.referenceAssetIds),
    logoAssetId: profile.logoAssetId || null,
    avoidVisuals: cleanArray(profile.avoidVisuals),
    preferredImageMood: profile.preferredImageMood || null,
    imageCompositionHints: cleanArray(profile.imageCompositionHints),
    preferredPlatforms: splitCsv(profile.preferredPlatformsText, []).map(normalizePlatformName).filter(Boolean),
    platformPriority: splitCsv(profile.platformPriorityText, []).map(normalizePlatformName).filter(Boolean),
    perPlatformTone: linesToObject(profile.perPlatformToneText),
    perPlatformFormatPreferences: linesToObject(profile.perPlatformFormatPreferencesText, true),
    socialConnectStatus: profile.socialConnectStatus || {},
    websiteUrl: profile.websiteUrl || null,
    websiteAnalysis: profile.websiteAnalysis || null,
    referenceAssets: Array.isArray(profile.referenceAssets) ? profile.referenceAssets : [],
    language: profile.language || 'en',
    timezone: profile.timezone || 'UTC',
  };
}

export function cleanArray(value) {
  return Array.isArray(value) ? value.map((item) => String(item || '').trim()).filter(Boolean) : [];
}

export function objectToLines(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return '';
  return Object.entries(value)
    .map(([key, item]) => `${key}: ${Array.isArray(item) ? item.join(', ') : item}`)
    .join('\n');
}

export function linesToObject(value, arrayValues = false) {
  return String(value || '').split('\n').reduce((result, line) => {
    const [rawKey, ...rest] = line.split(':');
    const key = normalizePlatformName(rawKey);
    const rawValue = rest.join(':').trim();
    if (!key || !rawValue) return result;
    result[key] = arrayValues ? splitCsv(rawValue, []) : rawValue;
    return result;
  }, {});
}

export function isBrandProfileReady(profile) {
  return (
    String(profile.businessDescription || '').trim().length >= 5 &&
    String(profile.targetAudience || '').trim().length >= 3
  );
}

export function defaultPublishingPreferences() {
  return {
    autopilotMode: 'approval',
    postsPerWeek: 3,
    preferredDaysText: 'monday, wednesday, friday',
    preferredTimesText: '10:00',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    requireApprovalForSalesContent: true,
    requireApprovalForNewPlatforms: true,
  };
}

export function toPublishingForm(preferences = {}) {
  return {
    ...defaultPublishingPreferences(),
    autopilotMode: preferences.autopilotMode || 'approval',
    postsPerWeek: preferences.postsPerWeek || 3,
    preferredDaysText: (preferences.preferredDays || ['monday', 'wednesday', 'friday']).join(', '),
    preferredTimesText: (preferences.preferredTimes || ['10:00']).join(', '),
    timezone: preferences.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    requireApprovalForSalesContent: preferences.requireApprovalForSalesContent ?? true,
    requireApprovalForNewPlatforms: preferences.requireApprovalForNewPlatforms ?? true,
  };
}

export function fromPublishingForm(preferences) {
  return {
    autopilotMode: preferences.autopilotMode,
    postsPerWeek: Number(preferences.postsPerWeek) || 3,
    preferredDays: splitCsv(preferences.preferredDaysText, ['monday', 'wednesday', 'friday']),
    preferredTimes: splitCsv(preferences.preferredTimesText, ['10:00']),
    timezone: preferences.timezone || 'UTC',
    requireApprovalForSalesContent: !!preferences.requireApprovalForSalesContent,
    requireApprovalForNewPlatforms: !!preferences.requireApprovalForNewPlatforms,
  };
}

export function splitCsv(value, fallback) {
  const values = String(value || '').split(',').map((item) => item.trim()).filter(Boolean);
  return values.length ? values : fallback;
}

export function normalizePlatformName(value) {
  const normalized = String(value || '').trim().toLowerCase();
  const aliased = normalized === 'twitter' ? 'x' : normalized;
  return ['telegram', 'linkedin', 'instagram', 'threads', 'tiktok', 'facebook', 'x'].includes(aliased) ? aliased : null;
}

export function nextMondayIsoDate() {
  const date = new Date();
  const offset = (8 - date.getDay()) % 7 || 7;
  date.setDate(date.getDate() + offset);
  return date.toISOString().slice(0, 10);
}
